/**
 * Locale Detection Service
 *
 * Best-effort auto-detection of a visitor's country and language for
 * pre-filling the registration form. Layered, fastest/most-reliable first:
 *
 *   1. Browser language (navigator.language) — instant, no network.
 *   2. IP geolocation (ipwho.is — keyless, free, no rate limit for our use)
 *      — gives country ISO2.
 *   3. Browser timezone (Intl.DateTimeFormat().resolvedOptions().timeZone) —
 *      only used as a fallback signal for the country.
 *
 * Detection is advisory only: it PRE-FILLS the form, never overrides a user
 * selection, and every layer can fail silently — callers get nulls and keep
 * their own defaults (Spain/Barcelona).
 */

import { PICKER_LOCALE_CODE_SET } from '../constants/locales';

/** Minimal shape of the ipwho.is response we consume. */
interface IpWhoResponse {
  success?: boolean;
  country_code?: string;
  city?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
}

export interface DetectedLocale {
  /** BCP 47 tag from the browser (e.g. "es-ES"), null when undetectable. */
  language: string | null;
  /** ISO2 country code (e.g. "ES"), null when undetectable. */
  countryCode: string | null;
  /** State/region name as spelled in geo_states ("Barcelona"), null when unknown. */
  stateName: string | null;
}

/** Uppercase ISO2 or null. */
const normalizeIso2 = (value: unknown): string | null =>
  typeof value === 'string' && /^[a-zA-Z]{2}$/.test(value.trim())
    ? value.trim().toUpperCase()
    : null;

/**
 * Resolve a browser locale (possibly unsupported, e.g. "vi-VN") into the best
 * picker code: exact picker match → picker variant of the same language
 * (en-AU → en-GB) → null (caller keeps its default). Stays within the curated
 * 25-language picker list so the pre-filled value is always visible in the UI.
 */
export function resolveBrowserLocale(browserLocale: string | null | undefined): string | null {
  if (!browserLocale) return null;
  const tag = browserLocale.trim();
  if (!tag) return null;

  if (PICKER_LOCALE_CODE_SET.has(tag)) return tag;

  // Case-insensitive exact ("en-gb" → "en-GB").
  const lower = tag.toLowerCase();
  for (const code of PICKER_LOCALE_CODE_SET) {
    if (code.toLowerCase() === lower) return code;
  }

  // Language-only: "en" or "en-AU" → first picker "en-*" variant.
  const lang = tag.split(/[-_]/)[0].toLowerCase();
  if (!lang) return null;
  for (const code of PICKER_LOCALE_CODE_SET) {
    if (code.toLowerCase().split('-')[0] === lang) return code;
  }
  return null;
}

/** Browser locale from navigator, resolved against the supported set. */
function detectFromNavigator(): { language: string | null; countryCode: string | null } {
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    if (!nav?.language) return { language: null, countryCode: null };

    const language = resolveBrowserLocale(nav.language);
    // A regional browser locale ("es-AR") also hints at the country.
    const parts = nav.language.split(/[-_]/);
    const countryCode = parts.length > 1 ? normalizeIso2(parts[1]) : null;
    return { language, countryCode };
  } catch {
    return { language: null, countryCode: null };
  }
}

/** IP geolocation via ipwho.is (keyless). Time-bounded, never throws. */
async function detectFromIp(): Promise<{ countryCode: string | null; stateName: string | null } | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 3500);
  try {
    const res = await fetch('https://ipwho.is/', { signal: controller.signal });
    if (!res.ok) return null;
    const data: IpWhoResponse = await res.json();
    if (data.success === false) return null;
    return {
      countryCode: normalizeIso2(data.country_code),
      // ipwho.is returns ISO codes for region in `region_code`, but we keep
      // the human name — geo_states stores state names, not codes.
      stateName: typeof data.region === 'string' && data.region.trim() ? data.region.trim() : null,
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Detect the visitor's locale signals. Returns quickly (browser signals are
 * synchronous); the IP lookup adds ≤3.5s worst case. Never throws.
 */
export async function detectLocale(): Promise<DetectedLocale> {
  const fromNav = detectFromNavigator();

  // Fast path: if the browser locale already carries a region ("es-ES"),
  // it's the strongest single signal — use it without the IP call.
  if (fromNav.countryCode) {
    return { language: fromNav.language, countryCode: fromNav.countryCode, stateName: null };
  }

  // No regional hint from the browser → IP lookup for country + region.
  const fromIp = await detectFromIp();
  if (fromIp?.countryCode) {
    return {
      language: fromNav.language,
      countryCode: fromIp.countryCode,
      stateName: fromIp.stateName,
    };
  }

  // Everything failed → caller keeps its own defaults.
  return { language: fromNav.language, countryCode: null, stateName: null };
}
